/* eslint-disable no-param-reassign */
import { produce } from 'immer';
import dayjs from 'dayjs';

import { AppLeaf, AppGroupLeaf } from 'types/common/leaf';
import { LightState } from 'types/aspen/state';
import { OptimisticAction } from 'types/actions/optimistic';
import { deviceLeafReducer } from './deviceLeaf';

// Generic helper to update member devices based on action source
const updateMemberDevices = (
  draft: AppLeaf,
  action: OptimisticAction,
  updateFn: (member: AppLeaf, leafAction: OptimisticAction) => void,
) => {
  // Type guard to ensure we're working with a group leaf
  if (draft.type !== 'group') return;

  // TypeScript now knows draft is AppGroupLeaf
  const groupDraft = draft as AppGroupLeaf;

  if (action.origin.type === 'group') {
    // Group action: update ALL member devices
    Object.values(groupDraft.memberLeaves).forEach((member) => {
      updateFn(member, action);
    });
  } else if (action.origin.type === 'device' && action.origin.id) {
    // Device action: update SPECIFIC member device using device reducer
    const memberDevice = groupDraft.memberLeaves[action.origin.id];
    if (memberDevice && memberDevice.type === 'device') {
      const updatedDevice = deviceLeafReducer(memberDevice, action);
      // Type assertion is safe because we know deviceLeafReducer returns the same type it receives
      groupDraft.memberLeaves[action.origin.id] = updatedDevice as typeof memberDevice;
    }
  }
};

// Generic helper to update group state
const updateGroupState = (
  draft: AppLeaf,
  action: OptimisticAction,
  updateFn: (groupState: AppLeaf['state'], leafAction: OptimisticAction) => void,
) => {
  updateFn(draft.state, action);
};

export const groupLeafReducer = (state: AppLeaf, action: OptimisticAction): AppLeaf => {
  // Early return if not a group leaf
  if (state.type !== 'group') {
    return state;
  }

  switch (action.type) {
    case 'SET_BRIGHTNESS':
      return produce(state, (draft) => {
        // Update member devices - device reducer handles device actions automatically
        updateMemberDevices(draft, action, (member) => {
          if ('brightness' in member.state && member.state.brightness) {
            member.state.brightness.updated = dayjs().unix();
            member.state.brightness.level = action.payload;
          }
          // Auto-turn-on will be handled via dispatch side-effect
        });

        // Update group state - different logic for group vs device actions
        if (action.origin.type === 'group') {
          // Group action: set group brightness directly and turn on
          updateGroupState(draft, action, (groupState) => {
            if ('brightness' in groupState && groupState.brightness) {
              groupState.brightness.updated = dayjs().unix();
              groupState.brightness.level = action.payload;
            }
            // Auto-turn-on group when brightness changes
            if (groupState?.power) {
              groupState.power.updated = dayjs().unix();
              groupState.power.power = 'on';
            }
          });
        } else if (action.origin.type === 'device' && draft.type === 'group') {
          // Device action: calculate group brightness as average of all members
          const groupDraft = draft as AppGroupLeaf;
          const memberBrightnessLevels = Object.values(groupDraft.memberLeaves)
            .map((member) =>
              member && typeof member === 'object' && 'state' in member
                ? (member.state as LightState)?.brightness?.level
                : undefined,
            )
            .filter((level): level is number => typeof level === 'number');

          if (memberBrightnessLevels.length > 0) {
            const averageBrightness = Math.round(
              memberBrightnessLevels.reduce((sum, level) => sum + level, 0) / memberBrightnessLevels.length,
            );

            if ('brightness' in draft.state && draft.state.brightness) {
              draft.state.brightness.updated = dayjs().unix();
              draft.state.brightness.level = averageBrightness;
            }

            // Auto-turn-on group when any member brightness changes (since member was already turned on)
            if (draft.state?.power) {
              draft.state.power.updated = dayjs().unix();
              draft.state.power.power = 'on';
            }
          }
        }
      });
    case 'SET_POWER': {
      const result = produce(state, (draft) => {
        // Update member devices - device reducer handles device actions automatically
        updateMemberDevices(draft, action, (member) => {
          if (member.state?.power) {
            member.state.power.updated = dayjs().unix();
            member.state.power.power = action.payload;
          }
        });

        // Update group state - different logic for group vs device actions
        if (action.origin.type === 'group') {
          // Group action: set group power directly
          updateGroupState(draft, action, (groupState) => {
            if (groupState?.power) {
              groupState.power.updated = dayjs().unix();
              groupState.power.power = action.payload;
            }
          });
        } else if (action.origin.type === 'device' && draft.type === 'group') {
          // Device action: calculate group power based on all members
          const groupDraft = draft as AppGroupLeaf;
          const memberPowerStates = Object.values(groupDraft.memberLeaves).map((member) =>
            member && typeof member === 'object' && 'state' in member
              ? (member.state as LightState)?.power?.power
              : undefined,
          );
          const anyMemberOn = memberPowerStates.some((power) => power === 'on');
          const groupPowerState = anyMemberOn ? 'on' : 'off';

          if (draft.state?.power) {
            draft.state.power.updated = dayjs().unix();
            draft.state.power.power = groupPowerState;
          }
        }
      });
      return result;
    }
    case 'SET_COLOR':
      return produce(state, (draft) => {
        // Update member devices - device reducer handles device actions automatically
        updateMemberDevices(draft, action, (member) => {
          if ('color' in member.state && member.state.color) {
            member.state.color.updated = dayjs().unix();
            Object.assign(member.state.color, action.payload);
          }
          // Auto-turn-on will be handled via dispatch side-effect
        });

        // Update group state - different logic for group vs device actions
        if (action.origin.type === 'group') {
          // Group action: set group color directly and turn on
          updateGroupState(draft, action, (groupState) => {
            if ('color' in groupState && groupState.color) {
              groupState.color.updated = dayjs().unix();
              Object.assign(groupState.color, action.payload);
            }
            // Auto-turn-on group when color changes
            if (groupState?.power) {
              groupState.power.updated = dayjs().unix();
              groupState.power.power = 'on';
            }
          });
        } else if (action.origin.type === 'device' && draft.type === 'group') {
          // Device action: update group color to match the changed device and turn on group
          if ('color' in draft.state && draft.state.color) {
            draft.state.color.updated = dayjs().unix();
            Object.assign(draft.state.color, action.payload);
          }

          if (draft.state?.power) {
            draft.state.power.updated = dayjs().unix();
            draft.state.power.power = 'on';
          }
        }
      });
    default:
      return state;
  }
};
